import { useState } from '#app';

import type { DehydratedState, VueQueryPluginOptions } from '@tanstack/vue-query';
import { VueQueryPlugin, QueryClient, hydrate, dehydrate } from '@tanstack/vue-query';

export default defineNuxtPlugin({
    name: 'vueQuery',
    setup(nuxtApp) {
        const vueQueryState = useState<DehydratedState | null>('vueQuery');

        const queryClient = new QueryClient({
            defaultOptions: {
                queries: {
                    // Retry only when no HTTP response came back at all: a server that answered has
                    // given its verdict and asking again will not change it, while an unreachable
                    // server is usually a transient blip. This is also the only way a page heals
                    // itself, because after a spurious `online` event onlineManager stays online, so
                    // a genuine reconnection fires no further event for refetchOnReconnect to act on.
                    // The import.meta.client guard is load bearing: an explicit function replaces the
                    // built-in `isServer ? 0 : 3` default, which would otherwise add up to 7 seconds
                    // of backoff to every SSR render. retryDelay is left at the default exponential
                    // backoff, 1s doubling up to 30s.
                    retry: (failureCount, error): boolean => import.meta.client && failureCount < 3 && isConnectivityError(error),
                    refetchOnWindowFocus: false,
                    staleTime: 5000, // To avoid client re-fetch after SSR
                    // Other default values:
                    // - cacheTime: 5 min
                },
            },
        });
        const options: VueQueryPluginOptions = { queryClient, enableDevtoolsV6Plugin: true };

        nuxtApp.vueApp.use(VueQueryPlugin, options);

        if (import.meta.server) {
            nuxtApp.hooks.hook('app:rendered', () => {
                vueQueryState.value = dehydrate(queryClient);
            });
        }

        if (import.meta.client) {
            nuxtApp.hooks.hook('app:created', () => {
                hydrate(queryClient, vueQueryState.value);
            });
        }
    },
});
